import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  IconButton,
  Divider,
  Chip,
  CircularProgress,
  LinearProgress,
  List,
  ListItem,
  ListItemText
} from '@mui/material'
import {
  Close as CloseIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material'
import { Cigar, useBulkMoveCigarsMutation } from '@store/api/cigarApi'
import { useGetHumidorsQuery, Humidor } from '@store/api/humidorApi'

interface BulkMoveCigarDialogProps {
  open: boolean
  onClose: () => void
  cigars: Cigar[]
}

const BulkMoveCigarDialog: React.FC<BulkMoveCigarDialogProps> = ({ open, onClose, cigars }) => {
  const [destinationHumidorId, setDestinationHumidorId] = useState('')
  const [error, setError] = useState('')
  const [step, setStep] = useState<'select' | 'preview' | 'success'>('select')
  const [resultMessage, setResultMessage] = useState('')

  const { data: humidors = [] } = useGetHumidorsQuery()
  const [bulkMoveCigars, { isLoading }] = useBulkMoveCigarsMutation()

  const handleClose = () => {
    setDestinationHumidorId('')
    setError('')
    setStep('select')
    setResultMessage('')
    onClose()
  }

  const handlePreview = () => {
    if (!destinationHumidorId) return
    setError('')
    setStep('preview')
  }

  const handleBack = () => {
    setStep('select')
    setError('')
  }

  const handleConfirm = async () => {
    if (!destinationHumidorId || cigars.length === 0) return
    setError('')

    try {
      const result = await bulkMoveCigars({
        cigarIds: cigars.map(c => c.id),
        destinationHumidorId
      }).unwrap()
      setResultMessage(result.message || `Moved ${result.movedCount} cigar(s) successfully`)
      setStep('success')
      setTimeout(() => {
        handleClose()
      }, 2000)
    } catch (err: any) {
      setError(err?.data?.message || 'Failed to move cigars. Please try again.')
      setStep('select')
    }
  }

  const availableHumidors = (Array.isArray(humidors) ? humidors : []).filter(
    (h: any) => !h.isArchived
  ) as Humidor[]

  const destHumidor = availableHumidors.find(h => h.id === destinationHumidorId)
  const destCurrentCount = destHumidor?._count?.cigars ?? destHumidor?.cigarCount ?? 0
  const destCapacity = destHumidor?.capacity ?? 0

  const cigarsToMove = cigars.filter(c => c.humidorId !== destinationHumidorId)
  const alreadyInDest = cigars.length - cigarsToMove.length

  const wouldExceedCapacity = destCapacity > 0 && destCurrentCount + cigarsToMove.length > destCapacity
  const destAvailable = destCapacity > 0 ? destCapacity - destCurrentCount : null
  const destUsagePercent = destCapacity > 0 ? (destCurrentCount / destCapacity) * 100 : 0

  if (cigars.length === 0) return null

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Move {cigars.length} Cigar{cigars.length !== 1 ? 's' : ''}</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {step === 'select' && (
          <>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {cigars.length} cigar{cigars.length !== 1 ? 's' : ''} selected for movement
            </Typography>

            <FormControl fullWidth sx={{ mt: 2 }} required>
              <InputLabel>Destination Humidor</InputLabel>
              <Select
                value={destinationHumidorId}
                label="Destination Humidor"
                onChange={(e) => setDestinationHumidorId(e.target.value)}
              >
                {availableHumidors.map((h) => {
                  const count = h._count?.cigars ?? h.cigarCount ?? 0
                  const isFull = h.capacity > 0 && count >= h.capacity
                  return (
                    <MenuItem key={h.id} value={h.id} disabled={isFull}>
                      {h.name} ({count}/{h.capacity}){isFull ? ' — Full' : ''}
                    </MenuItem>
                  )
                })}
              </Select>
            </FormControl>

            {availableHumidors.length === 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                No humidors available. Create a humidor first.
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </>
        )}

        {step === 'preview' && destHumidor && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Movement Summary
            </Typography>

            <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
              <Box flex={1} p={1.5} bgcolor="background.default" borderRadius={1} textAlign="center">
                <Typography variant="caption" color="text.secondary" display="block">Moving to</Typography>
                <Typography variant="body2" fontWeight="bold">{destHumidor.name}</Typography>
              </Box>
              <Chip label={`${cigarsToMove.length} cigar${cigarsToMove.length !== 1 ? 's' : ''}`} color="primary" />
            </Box>

            {alreadyInDest > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {alreadyInDest} cigar{alreadyInDest !== 1 ? 's are' : ' is'} already in this humidor and will be skipped.
              </Alert>
            )}

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" gutterBottom>Destination Capacity</Typography>
            <Box sx={{ mb: 1 }}>
              <Box display="flex" justifyContent="space-between" mb={0.5}>
                <Typography variant="body2">
                  {destCurrentCount} → {destCurrentCount + cigarsToMove.length} / {destCapacity}
                </Typography>
                {destAvailable !== null && (
                  <Chip
                    size="small"
                    label={`${destAvailable} available`}
                    color={wouldExceedCapacity ? 'error' : destAvailable <= 5 ? 'warning' : 'success'}
                  />
                )}
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min(((destCurrentCount + cigarsToMove.length) / destCapacity) * 100, 100)}
                color={wouldExceedCapacity ? 'error' : destUsagePercent > 80 ? 'warning' : 'primary'}
                sx={{ height: 8, borderRadius: 1 }}
              />
            </Box>

            {wouldExceedCapacity && (
              <Alert severity="error" sx={{ mt: 1 }}>
                Cannot move {cigarsToMove.length} cigars — only {destAvailable} space available.
              </Alert>
            )}

            {cigarsToMove.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>Cigars to Move</Typography>
                <List dense disablePadding sx={{ maxHeight: 160, overflow: 'auto' }}>
                  {cigarsToMove.slice(0, 10).map(c => (
                    <ListItem key={c.id} disablePadding>
                      <ListItemText
                        primary={`${c.brand?.name} ${c.cigarLine?.name}`}
                        secondary={`From: ${c.humidor?.name} • Qty: ${c.quantity}`}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  ))}
                  {cigarsToMove.length > 10 && (
                    <ListItem disablePadding>
                      <ListItemText
                        primary={`+ ${cigarsToMove.length - 10} more`}
                        primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                      />
                    </ListItem>
                  )}
                </List>
              </>
            )}

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        )}

        {step === 'success' && (
          <Box display="flex" flexDirection="column" alignItems="center" py={2} gap={1}>
            <CheckIcon color="success" sx={{ fontSize: 48 }} />
            <Typography variant="body1" color="success.main" textAlign="center">
              {resultMessage}
            </Typography>
          </Box>
        )}
      </DialogContent>

      {step !== 'success' && (
        <DialogActions sx={{ px: 3, py: 2 }}>
          {step === 'preview' ? (
            <>
              <Button onClick={handleBack} color="inherit" disabled={isLoading}>
                Back
              </Button>
              <Button
                onClick={handleConfirm}
                variant="contained"
                disabled={isLoading || wouldExceedCapacity || cigarsToMove.length === 0}
                startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
              >
                {isLoading ? 'Moving...' : `Move ${cigarsToMove.length} Cigar${cigarsToMove.length !== 1 ? 's' : ''}`}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleClose} color="inherit">
                Cancel
              </Button>
              <Button
                onClick={handlePreview}
                variant="contained"
                disabled={!destinationHumidorId}
              >
                Preview Move
              </Button>
            </>
          )}
        </DialogActions>
      )}
    </Dialog>
  )
}

export default BulkMoveCigarDialog
